import { useEffect, useState } from "react";
import { FaClock } from "react-icons/fa";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { canEditMeasurementSchedule } from "./installationConfigUtils";
import { getScheduleModePickerSubtitle } from "./installationCronUtils";
import {
  buildCronStateForScheduleMode,
  SCHEDULE_MODE_BATTERY,
  SCHEDULE_MODE_INSTALLATION,
  SCHEDULE_MODE_PRODUCTION,
} from "./installationScheduleModeUtils";
import {
  InstallationPickerOption,
  InstallationPickerRow,
  InstallationPickerSheet,
} from "./InstallationPickerSheet";

const InstallationCronSection = ({
  routeCameraId,
  codeVersion,
  cronState,
  onCronSave,
  openDetailsPage,
  divided,
  disabled,
  saving,
  adminOnly = false,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [modeSheetOpen, setModeSheetOpen] = useState(false);
  const [draftScheduleMode, setDraftScheduleMode] = useState(
    cronState.scheduleMode,
  );
  const canEdit = canEditMeasurementSchedule(codeVersion);
  const modeSubtitle = getScheduleModePickerSubtitle(t, cronState);
  const canOpenDetails = openDetailsPage && Boolean(routeCameraId);

  useEffect(() => {
    if (modeSheetOpen) {
      setDraftScheduleMode(cronState.scheduleMode);
    }
  }, [modeSheetOpen, cronState.scheduleMode]);

  if (!canEdit) {
    return (
      <p className="installation-settings-static-note installation-settings-static-note-divided">
        {t("installation_cron_version_blocked")}
      </p>
    );
  }

  const modeDirty = draftScheduleMode !== cronState.scheduleMode;

  const closeModeSheet = () => {
    setDraftScheduleMode(cronState.scheduleMode);
    setModeSheetOpen(false);
  };

  const saveMode = async () => {
    const nextState = buildCronStateForScheduleMode(
      cronState,
      draftScheduleMode,
    );
    const saved = await onCronSave(nextState);
    if (saved) {
      setModeSheetOpen(false);
    }
  };

  return (
    <>
      <InstallationPickerRow
        icon={FaClock}
        iconVariant="schedule"
        title={t("installation_schedule_mode_label")}
        subtitle={modeSubtitle}
        onClick={() => {
          if (canOpenDetails) {
            history.push(`/app/installationdetails/${routeCameraId}/schedule`);
            return;
          }
          setModeSheetOpen(true);
        }}
        disabled={disabled || saving}
        divided={divided}
        adminOnly={adminOnly}
      />

      <InstallationPickerSheet
        open={!canOpenDetails && modeSheetOpen}
        title={t("installation_picker_title_mode")}
        dirty={modeDirty}
        saving={saving}
        onDismiss={closeModeSheet}
        onSave={() => void saveMode()}
      >
        <InstallationPickerOption
          label={t("installation_schedule_mode_production")}
          description={t("installation_schedule_mode_production_desc")}
          selected={draftScheduleMode === SCHEDULE_MODE_PRODUCTION}
          onSelect={() => setDraftScheduleMode(SCHEDULE_MODE_PRODUCTION)}
        />
        <InstallationPickerOption
          label={t("installation_schedule_mode_battery")}
          description={t("installation_schedule_mode_battery_desc")}
          selected={draftScheduleMode === SCHEDULE_MODE_BATTERY}
          onSelect={() => setDraftScheduleMode(SCHEDULE_MODE_BATTERY)}
        />
        <InstallationPickerOption
          label={t("installation_schedule_mode_installation")}
          description={t("installation_schedule_mode_installation_desc")}
          selected={draftScheduleMode === SCHEDULE_MODE_INSTALLATION}
          onSelect={() => setDraftScheduleMode(SCHEDULE_MODE_INSTALLATION)}
        />
      </InstallationPickerSheet>
    </>
  );
};

export default InstallationCronSection;
